import { useCallback, useRef, useState } from "react";
import dayjs from "dayjs";
import { useFirebase } from "./useFirebase";

// One query on createdAt only: needs no composite index; other filters run in the browser
export const MAILS_LIMIT = 500;

// Written by functions/src/Mail.ts for every send attempt
export interface MailLog {
  id: string;
  type: string;
  subject: string;
  to: string[];
  cc: string[];
  status: "sent" | "failed";
  messageId: string | null;
  error: string | null;
  company: { id: string; name: string | null } | null;
  transactionId: string | null;
  user: { userId: string | null; name: string | null; surname: string | null };
  createdAt: Timestamp;
}

export const useMails = () => {
  const { db } = useFirebase();
  const [mails, setMails] = useState<MailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const latest = useRef(0);

  // from / to: "YYYY-MM-DD" (local days, "to" inclusive)
  const fetch = useCallback(
    async (from: string, to: string) => {
      const request = ++latest.current; // a slower, older read must not overwrite a newer one
      setLoading(true);
      setError(false);
      try {
        const snap = await db
          .collection("mails")
          .where("createdAt", ">=", dayjs(from).toDate())
          .where("createdAt", "<", dayjs(to).add(1, "day").toDate())
          .orderBy("createdAt", "desc")
          .limit(MAILS_LIMIT)
          .get();
        if (request !== latest.current) return;
        setMails(snap.docs.map((doc: any) => ({ id: doc.id, ...doc.data() })));
      } catch (e) {
        console.error("useMails", e);
        if (request !== latest.current) return;
        setMails([]);
        setError(true);
      }
      setLoading(false);
    },
    [db]
  );

  return { mails, loading, error, truncated: mails.length === MAILS_LIMIT, fetch };
};
